import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPES = [["acompte", "Acompte"], ["solde", "Solde"], ["complementaire", "Paiement complémentaire"], ["autre", "Autre"]];
const METHODS = [["paypal", "PayPal"], ["cb", "CB"], ["virement", "Virement"], ["especes", "Espèces"], ["autre", "Autre"]];
const EMPTY = { amount: "", paid_at: new Date().toISOString().slice(0, 10), type: "acompte", method: "virement", note: "", urssaf_free: false };

export default function PaymentsSection({ rid }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/reservations/${rid}/payments`).then((r) => setData(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!(parseFloat(form.amount) > 0) || !form.paid_at) { toast.error("Montant et date requis"); return; }
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/reservations/${rid}/payments/${editId}`, { ...form, amount: parseFloat(form.amount) });
        toast.success("Paiement modifié");
      } else {
        await api.post(`/reservations/${rid}/payments`, { ...form, amount: parseFloat(form.amount) });
        toast.success("Encaissement enregistré");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const del = async (pid, type) => {
    const msg = type === "solde"
      ? "Remettre ce solde en règlement en attente ? Le montant sera immédiatement retiré du CA encaissé, repassera en « Reste à encaisser » et l'action restera tracée dans l'historique."
      : "Annuler la validation de cet encaissement ? Le CA sera recalculé immédiatement et l'action restera tracée dans l'historique.";
    if (!window.confirm(msg)) return;
    await api.delete(`/reservations/${rid}/payments/${pid}`);
    toast.success(type === "solde" ? "Solde remis en règlement en attente — CA recalculé" : "Encaissement annulé — CA recalculé");
    load();
  };

  const migrate = async () => {
    const r = await api.post(`/reservations/${rid}/payments/migrate`);
    setData(r.data);
    toast.success("Montants de la fiche convertis en lignes modifiables");
  };

  if (!data) return null;
  return (
    <section data-testid="payments-section" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg font-bold">Paiements / Encaissements</h2>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Prestation</p>
          <p className="font-mono tabular-nums font-bold" data-testid="payments-total">{fmtEUR(data.total)}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Encaissé</p>
          <p className="font-mono tabular-nums font-bold text-emerald-700" data-testid="payments-encaisse">{fmtEUR(data.encaisse)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Reste à encaisser</p>
          <p className="font-mono tabular-nums font-bold text-amber-700" data-testid="payments-reste">{fmtEUR(data.reste)}</p>
        </div>
      </div>

      {data.lines.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="payments-list">
          {data.lines.map((p, i) => (
            <li key={p.id} data-testid={`payment-line-${i}`} className="py-2 flex items-center gap-2 text-sm">
              <span title={p.urssaf_free ? "Paiement encaissé — Hors CA déclaré / URSSAF" : undefined} className={`shrink-0 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.urssaf_free ? "bg-violet-100 text-violet-700" : p.type === "acompte" ? "bg-sky-100 text-sky-700" : p.type === "solde" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                {(TYPES.find(([v]) => v === p.type) || [])[1] || p.type}
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-mono tabular-nums font-semibold">{fmtEUR(p.amount)}</span>
                <span className="text-slate-500"> — le {fmtDate(p.paid_at)}</span>
                {p.method && <span className="text-slate-500"> · {(METHODS.find(([v]) => v === p.method) || [])[1] || p.method}</span>}
                {p.note ? <span className="text-slate-400"> · {p.note}</span> : null}
                {p.urssaf_free && <span className="ml-1 rounded bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[9px] font-bold uppercase">hors URSSAF</span>}
                {p.virtual && <span className="ml-1 rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[9px] font-bold uppercase">issu de la fiche</span>}
              </span>
              <span className="flex items-center gap-1 shrink-0">
                {!p.virtual && (
                  <Button variant="ghost" size="icon" data-testid={`payment-edit-${i}`} className="h-7 w-7 text-slate-500"
                    onClick={() => { setEditId(p.id); setForm({ amount: String(p.amount), paid_at: p.paid_at, type: p.type, method: p.method || "virement", note: p.note || "", urssaf_free: !!p.urssaf_free }); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {p.type === "solde" ? (
                  <button type="button" data-testid={`payment-reset-solde-${i}`} onClick={() => del(p.id, "solde")}
                    className="rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase hover:bg-amber-100 transition-colors">
                    Remettre en règlement en attente
                  </button>
                ) : !p.virtual && (
                  <Button variant="ghost" size="icon" data-testid={`payment-delete-${i}`} className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                    onClick={() => del(p.id, p.type)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data.lines.some((p) => p.virtual) && (
        <button type="button" data-testid="payments-migrate" onClick={migrate}
          className="text-xs font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-800">
          Rendre les montants de la fiche modifiables/supprimables ici
        </button>
      )}

      {data.reste > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {!data.lines.some((p) => p.type === "solde") && (
            <span data-testid="solde-status-pending" className="rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase">Solde : règlement en attente</span>
          )}
          <button type="button" data-testid="payment-quick-solde"
            onClick={() => setForm({ ...EMPTY, amount: String(data.reste), type: "solde" })}
            className="rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 px-3.5 py-1.5 text-xs font-bold hover:bg-emerald-100 transition-colors">
            ✓ Marquer le solde comme encaissé ({fmtEUR(data.reste)}) — date réelle demandée avant validation
          </button>
        </div>
      )}

      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3.5 space-y-2.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{editId ? "Modifier le paiement" : "Ajouter un encaissement"}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Input data-testid="payment-amount" type="number" min="0" step="0.01" placeholder="Montant €" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Input data-testid="payment-date" type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} />
          <select data-testid="payment-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 rounded-md border border-input bg-white px-2 text-sm">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="payment-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="h-10 rounded-md border border-input bg-white px-2 text-sm">
            {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <Input data-testid="payment-note" placeholder="Remarque (facultatif)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="payment-urssaf-free" checked={form.urssaf_free} onChange={(e) => setForm({ ...form, urssaf_free: e.target.checked })} className="h-4 w-4 accent-violet-600" />
          Ne pas appliquer la déduction URSSAF de 22,9 % sur ce montant
        </label>
        <div className="flex gap-2">
          <Button data-testid="payment-save" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editId ? "Enregistrer la modification" : "Ajouter l'encaissement"}
          </Button>
          {editId && <Button variant="ghost" data-testid="payment-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); }}><X className="h-4 w-4" /> Annuler</Button>}
        </div>
      </div>
    </section>
  );
}
